import { isPossiblePhoneNumber, isValidPhoneNumber } from "react-phone-number-input";
import {
  isValidEmail,
  isIndianNumber,
  containsEmojis,
  isNumeric,
  regexPatterns,
} from "lib/utils/validations";
import { FormatterTypes, LengthLimitType, ValidationItem } from "lib/types/validation";
import { capitalizeFirstCharacter, checkMandatoryFields, truncateNationalPhoneDigits } from "lib/utils/helpers";
import {
  AddressCardFields,
  ErrorType,
  ValidateAddressFieldsAgainstSchemaArgs,
  ValidateAddressFieldsArgs,
  ValidateFieldArgs,
} from "lib/types/address";
import { getAddressFields } from "./address";
import { AddressConfigType } from "lib/contexts/MerchantProvider";
import { FormField } from "lib/hooks/useForm";

export const applyValueFormatters = ({
  value = "",
  formatters,
}: {
  value: any;
  formatters: FormatterTypes[];
}) => {
  return formatters.reduce((formattedValue, formatter) => {
    switch (formatter) {
      case "UPPERCASE":
        return formattedValue.toUpperCase();

      case "FIRST_CHAR_LOWERCASE":
        return formattedValue.charAt(0).toLowerCase() + formattedValue.slice(1);

      case "CAPITALIZE":
        return capitalizeFirstCharacter(formattedValue);

      case "PHONE": {
        let phone = formattedValue;
        if (isIndianNumber(phone) && phone[3] === "0") {
          phone = phone.slice(0, 3) + phone.slice(4);
        }
        return truncateNationalPhoneDigits(phone);
      }

      case "CLEAN_EMOJIS":
        return formattedValue.replace(regexPatterns.hasEmoji, "");

      case "NUMERIC":
        return formattedValue.replace(regexPatterns.numeric, "");

      case "LETTERS_WITH_NO_SPACE":
        return formattedValue.replaceAll(regexPatterns.space, "");

      case "LETTERS_ONLY":
        return formattedValue.replace(regexPatterns.lettersOnly, "");

      case "LETTERS_AND_PERIOD":
        return formattedValue.replace(regexPatterns.lettersAndPeriod, "");

      case "ALPHA_NUMERIC":
        return formattedValue.replace(regexPatterns.alphaNumeric, "");

      case "NO_DIGITS":
        return formattedValue.replace(regexPatterns.digits, "");

      default:
        if (typeof formatter === "function") {
          return formatter(formattedValue);
        }
        return formattedValue;
    }
  }, value);
};

export const validateFieldAgainstSchema = ({
  value,
  fieldSchema,
  addressFields = {},
  t,
}: ValidateFieldArgs) => {
  
  if(value){
    value = String(value).trim();
  }
  if (!fieldSchema) return;

  for (const key of Object.keys(fieldSchema)) {
    const schemaItem = fieldSchema[key as keyof ValidationItem];

    if (!containsEmojis(value)) {
      value = applyValueFormatters({ value, formatters: ["CLEAN_EMOJIS"] });
    }

    switch (key) {
      case "formatters":
        applyValueFormatters({ value, formatters: schemaItem as FormatterTypes[] });
        break;

      case "type":
        if (schemaItem === "number" && typeof value !== "number")
          return { status: true, message: "Please enter a valid number" };
        break;

      case "required":
        if (!Boolean(value)) return { status: true, message: schemaItem as string };
        break;

      case "min":
        if (value.length < (schemaItem as LengthLimitType).limit)
          return { status: true, message: (schemaItem as LengthLimitType).message };
        break;

      case "max":
        if (value.length > (schemaItem as LengthLimitType).limit)
          return { status: true, message: (schemaItem as LengthLimitType).message };
        break;

      case "length":
        if (value.length !== (schemaItem as LengthLimitType).limit)
          return { status: true, message: (schemaItem as LengthLimitType).message };
        break;

      case "numeric":
        if (!isNumeric(value)) return { status: true, message: schemaItem as string };
        break;

      case "onlyLetters":
        if (regexPatterns.lettersOnly.test(value)) return { status: true, message: schemaItem as string };
        break;

      case "onlyLettersAndPeriod":
        if (regexPatterns.lettersAndPeriod.test(value))
          return { status: true, message: schemaItem as string };
        break;

      case "notSpecialCharactersOrNumbersOnly":
        if (regexPatterns.specialCharactersAndNumbersOnly.test(value))
          return { status: true, message: schemaItem as string };
        break;

      case "emailOrEmptyString":
        if (!regexPatterns.emailOrEmptyString.test(value || ""))
          return { status: true, message: schemaItem as string };
        break;

      case "email":
        if (!isValidEmail(value)) return { status: true, message: schemaItem as string };
        break;

      case "phone":
        if (isIndianNumber(value)) {
          if (value.length !== 13)
            return {
              status: true,
              message: t?.("indian_phone_number_digits_limit") || "Invalid Indian number",
            };
          if (value[3] < 6) return { status: true, message: schemaItem as string };
        }
        if (!isPossiblePhoneNumber(value) && !isValidPhoneNumber(value))
          return { status: true, message: schemaItem as string };
        break;

      case "when":
        if (typeof schemaItem === "function") {
          return schemaItem(value, addressFields);
        }
        break;

      case "label":
        break;

      default:
        throw new Error("Unexpected schema key: " + key);
    }
  }
};

export const validateSelectedAddress = ({
  selectedAddress,
  addressConfig,
  validationSchema,
  t,
}: {
  selectedAddress: AddressCardFields;
  addressConfig: AddressConfigType[];
  validationSchema?: Record<string, ValidationItem>;
  t?: (key: string) => string;
}) => {
  const schema = validationSchema || getAddressFields(addressConfig);

  const fieldErrors: Record<string, ErrorType> = {};
  const fieldKeys = Object.keys(schema);
  let isValid = true;

  for (const key of fieldKeys) {

    const fieldSchema = schema[key as keyof typeof schema];

    const error = validateFieldAgainstSchema({
      value: selectedAddress[key as keyof AddressCardFields],
      fieldSchema,
      addressFields: selectedAddress,
      t,
    });

    if (error) {
      isValid = false;
      fieldErrors[key] = error;
    }
  }

  return {
    available: isValid,
    errorFieldData: fieldErrors,
  };
};

export const validateAddressFields = ({ address, config, checkoutId }: ValidateAddressFieldsArgs) => {
  const mandatoryValidation = checkMandatoryFields(address, config, checkoutId);

  if (!mandatoryValidation.available) {
    return {
      available: mandatoryValidation.available,
      errorFieldData: mandatoryValidation.errorFieldData,
    };
  }

  const { available, errorFieldData } = validateSelectedAddress({
    selectedAddress: address,
    addressConfig: config,
  });

  if (!available) {
    return {
      available: available,
      errorFieldData: errorFieldData,
    };
  }

  return { available: true, errorFieldData: {} };
};
